import { useEffect, useState } from 'react';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, FormInstance, FormItemProps, UploadProps, message, Upload } from 'antd';
import Form, { Rule } from 'antd/es/form';
import { useFileUpload } from '../../../hooks/api/files';

import styles from './index.module.scss';

const { Dragger } = Upload;

interface FileProps extends FormItemProps {
  form: FormInstance;
  label?: string;
  uploadEndText?: string;
  name?: string;
  rules?: Rule[];
  displayDescription?: boolean;
  disabled?: boolean;
  defaultFileLocation?: string;
  allowDelete?: boolean;
}

export function InputFileDragger({
  form,
  name = 'fileId',
  rules = [],
  label,
  uploadEndText = 'Image',
  displayDescription = true,
  defaultFileLocation,
  disabled,
  allowDelete = false,
}: FileProps) {
  const [isIgnoreDefault, setIsIgnoreDefault] = useState(false); // Used to ignore defaultFileLocation
  const fileUpload = useFileUpload();

  useEffect(() => {
    if (fileUpload?.data?.id) {
      form.setFieldValue(name, fileUpload?.data?.id);
    }
  }, [fileUpload?.data?.id]);

  const imageUrl = fileUpload?.data?.location || (isIgnoreDefault ? undefined : defaultFileLocation);

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    action: (f) => new Promise(() => {
      fileUpload.fetchFile(f);
    }),
    onChange(info) {
      const { status } = info.file;

      if (status !== 'uploading') {
        console.log(info.file, info.fileList);
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Abort ongoing upload if any
    fileUpload.clearResponse();
    form.setFieldValue(name, null);
    // Clear internal AntD Upload file list (optional)
    form.validateFields([name]); // to trigger validation

    setIsIgnoreDefault(true); // Change state, to ignore 'default file' prop after deletion click
  };

  return (
    <Form.Item name={name} rules={rules} label={label}>
      <Dragger {...props} accept="image/*" disabled={disabled || fileUpload?.loading}>
        {fileUpload?.loading || !imageUrl ? (
          <div>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{`Click or drag file to this area to upload ${uploadEndText || ''}`}</p>
            {displayDescription ? (
              <p className="ant-upload-hint">
                Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned
                files.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <img src={imageUrl} alt="icon" style={{ width: '100%' }} />
            {allowDelete ? (
              <div className={styles.actions}>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={handleDelete} />
              </div>
            ) : null}
          </>
        )}
      </Dragger>
    </Form.Item>
  );
}

export default {
  InputFileDragger,
};
